'use client';

import {
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { OTPValidatorSchema } from '@/lib/validators/otp';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';

interface OTPInputGridProps {
    form: UseFormReturn<OTPValidatorSchema>;
    numDigits: number;
    otp: string[];
    setOtp: (otp: string[]) => void;
    inpRefs: React.RefObject<any>;
    setActiveBtnSubmit: (active: boolean) => void;
}

const OTPInputGrid: React.FC<OTPInputGridProps> = ({
    form,
    numDigits,
    otp,
    setOtp,
    inpRefs,
    setActiveBtnSubmit,
}) => {
    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number,
    ) => {
        if (
            (e.ctrlKey || e.metaKey) &&
            (e.key === 'v' || e.key === 'ท' || e.keyCode === 86)
        ) {
            return;
        }

        const allowedKeys = [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
        ];
        const isNumber = /^[0-9]$/.test(e.key);

        if (!isNumber && !allowedKeys.includes(e.key)) {
            e.preventDefault();
            return;
        }

        if (
            index > 0 &&
            e.key === 'Backspace' &&
            !otp[index] &&
            inpRefs?.current?.[index - 1]
        ) {
            // @ts-ignore
            inpRefs.current[index - 1]?.focus();
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number,
    ) => {
        const value = e.target.value.replace(/[^0-9]/g, '');

        if (value.length > 1) {
            const newOtp = [...otp];
            const pastedValues = value.split('').slice(0, numDigits);
            for (let i = 0; i < pastedValues.length; i++) {
                if (index + i < numDigits) {
                    newOtp[index + i] = pastedValues[i];
                    form.setValue(`otp_${index + i}`, pastedValues[i]);
                }
            }
            setOtp(newOtp);
            // @ts-ignore
            inpRefs?.current?.[index + pastedValues.length]?.focus();
            setActiveBtnSubmit(newOtp.join('').length === numDigits);
            return;
        }

        if (value && /^[0-9]$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            form.setValue(`otp_${index}`, value.charAt(0));
            // @ts-ignore
            inpRefs?.current?.[index + 1]?.focus();
            if (index === numDigits - 1) setActiveBtnSubmit(true);
        }
    };

    return (
        <div className="flex flex-row items-center justify-between mx-auto w-full gap-2 md:gap-3 xl:gap-5">
            {Array.from({ length: numDigits }).map((_, index) => (
                <div key={index} className="w-auto max-w-14 h-16">
                    <FormField
                        control={form.control}
                        name={`otp_${index}`}
                        render={({ field, formState }) => (
                            <FormItem>
                                <FormControl>
                                    <Input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        {...field}
                                        value={field.value || ''}
                                        onKeyDown={(e) =>
                                            handleKeyDown(e, index)
                                        }
                                        onChange={(e) => {
                                            if (e.target.value === '') {
                                                const newOtp = [...otp];
                                                newOtp[index] = '';
                                                setOtp(newOtp);
                                                form.setValue(
                                                    `otp_${index}`,
                                                    '',
                                                );
                                                setActiveBtnSubmit(
                                                    newOtp.every(
                                                        (digit) => digit !== '',
                                                    ),
                                                );
                                            } else {
                                                const val =
                                                    e.target.value.replace(
                                                        /[^0-9]/g,
                                                        '',
                                                    );
                                                form.setValue(
                                                    `otp_${index}`,
                                                    val.charAt(0),
                                                );
                                                handleChange(e, index);
                                            }
                                        }}
                                        className={cn(
                                            'form-input form-input-hide-arrow border-gray-200 text-center',
                                            formState.errors[`otp_${index}`] &&
                                                'form-input-error',
                                        )}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
            ))}
        </div>
    );
};

export default OTPInputGrid;
